import React from "react";
import PropTypes from "prop-types";
import useInteractiveObjects from "../hooks/useInteractiveObjects";
import interactiveRegistry from "../../../Registry/Interactive/InteractiveRegistry";
// Import the interactive components module as an object so we can lazily
// resolve component constructors by name. This avoids circular imports
// between the registry (which is imported by interactives) and the
// interactive components themselves.
import * as interactiveComponents from "@chartLib/lib/interactive";
import { DrawingObjectSelector } from "@chartLib/lib/interactive";
import {
  COMMON_EDGE_DEFAULTS,
  EXTENDED_EDGE_DEFAULTS,
  DEFAULT_APPEARANCE_COLORS,
} from "@chartLib/lib/interactive/commonDefaults";
import { useDrawingColorStore } from "../../../store/drawingColorStore";
import { useIndicatorDefaultsStore } from "../../../store/indicatorDefaultsStore";
import { useThemeStore, getThemeColors } from "../../../store/themeStore";
import {
  interactiveDefaultsKey,
  mergeCustomAppearance,
  resolveDrawingColor,
  hasCustomAppearance,
} from "../utils/interactiveAppearance";
import {
  getThemeAwareFillColor,
  getThemeAwareFillOpacity,
} from "../../../utils/colorUtils";
import { isDefined } from "@chartLib/lib/utils";
import { formatPrice as formatPriceGlobal } from "../../../utils/priceUtils";
import { addEventListenerAllWindows } from "~/utils/popoutWindowsRegistry";

// This layer owns the interactive hook and renders all registered interactives
function InteractiveLayer({
  components = {},
  appearanceMap = {},
  selectorEnabled = true,
  chartSectionId,
  chartState,
  chartSymbol = "",
  onInteractiveApiReady = null,
  onInteractiveContextMenu = null,
  onOpenEditModal = null,
  onDeleteInteractive = null,
  timeDisplayFormat,
  priceDisplayFormat,
  isLastSection = true,
}) {
  // Get the current drawing color from the store
  const currentDrawingColor = useDrawingColorStore(
    (state) => state.currentColor,
  );
  // Get the current theme for theme-aware fill colors
  const theme = useThemeStore((state) => state.theme);
  const themeColors = React.useMemo(() => getThemeColors(theme), [theme]);
  // derive components and default appearance from the registry using a generic flattening algorithm
  const registryAll = React.useMemo(() => {
    const flattened = {};

    // Iterate through all categories in the registry (drawing, tools, future categories...)
    Object.values(interactiveRegistry || {}).forEach((category) => {
      if (category && typeof category === "object" && category.interactives) {
        Object.assign(flattened, category.interactives);
      }
    });

    return flattened;
  }, []);

  // Resolve component constructors: allow consumers to override `components`.
  // The registry stores component names as strings to avoid circular imports;
  // resolve those names here against the imported `interactiveComponents` map.
  const registryComponents = Object.fromEntries(
    Object.values(registryAll).map((v) => {
      const compName = v.component;
      const resolved =
        typeof compName === "string"
          ? interactiveComponents[compName]
          : compName;
      return [v.key, resolved];
    }),
  );
  const safeComponents = components || {};
  const comps = Object.keys(safeComponents).length
    ? safeComponents
    : registryComponents;

  // build a simple appearance map from defaultOptionsForAppearance by taking each option's defaultValue
  const derivedAppearance = React.useMemo(() => {
    const appearances = {};
    Object.values(registryAll).forEach((it) => {
      const name = it.key;
      if (Array.isArray(it.defaultOptionsForAppearance)) {
        appearances[name] = it.defaultOptionsForAppearance.reduce(
          (acc, opt) => {
            if (!opt || !opt.name) return acc;
            const dv = opt.defaultValue;

            // Use registry format directly - no transformation
            acc[opt.name] = dv;
            return acc;
          },
          {},
        );

        // Apply appropriate edge defaults based on component needs
        // Components that need extended edge properties
        const needsExtendedDefaults = [
          "fibRetracement",
          "equidistantChannel",
        ].includes(name);
        const edgeDefaults = needsExtendedDefaults
          ? EXTENDED_EDGE_DEFAULTS
          : COMMON_EDGE_DEFAULTS;

        appearances[name] = {
          ...edgeDefaults,
          ...appearances[name],
        };
      }
    });
    return appearances;
  }, [registryAll]);

  const appearanceOverrides = appearanceMap || {};
  const appearances = Object.keys(appearanceOverrides || {}).length
    ? appearanceOverrides || {}
    : { ...derivedAppearance, ...(appearanceOverrides || {}) };

  // Issue #339: defaults customizados por tipo salvos pelo usuário
  // ("Usar como Padrão" no modal de edição). Muda apenas em save/reset.
  const customDefaults = useIndicatorDefaultsStore(
    (state) => state.customDefaults,
  );
  const customAppearanceByType = React.useMemo(() => {
    const map = {};
    Object.keys(registryAll).forEach((type) => {
      const saved =
        customDefaults[interactiveDefaultsKey(type)]?.appearanceConfig;
      if (hasCustomAppearance(saved)) map[type] = saved;
    });
    return map;
  }, [registryAll, customDefaults]);

  const registryKeysLength = React.useMemo(
    () => Object.keys(registryAll || {}).length,
    [registryAll],
  );

  // prefer provided interactiveApi (from ChartWrapper) to avoid duplicate hooks
  const hookApi = useInteractiveObjects({
    chartSectionId,
    chartId: chartState?.chartId || "default",
    chartState,
  });
  const {
    refsMap,
    objectsMap,
    onCompleteMap,
    onFinishDrawing,
    drawingObjectMap,
    getInteractiveNodes,
    handleDrawingObjectSelect,
    handleDrawingObjectDoubleClick,
    handleDrawingObjectContextMenu,
    registerInteractive,
    activeInteractive,
  } = hookApi;

  // Notify parent when interactive API is ready (used for main chart toolbar integration)
  React.useEffect(() => {
    if (typeof onInteractiveApiReady === "function" && hookApi) {
      onInteractiveApiReady(hookApi);
    }
  }, [hookApi, onInteractiveApiReady]);

  // optional: auto-register components that are provided but not registered yet
  React.useEffect(() => {
    Object.values(registryAll).forEach((entry) => {
      const type = entry.key;
      const objectsKey = entry.key;
      const propName = entry.propName ?? entry.key;
      if (!drawingObjectMap[type]) {
        registerInteractive({ type, objectsKey, propName, ref: refsMap[type] });
      }
    });
  }, [registryKeysLength, drawingObjectMap]);

  // Wrapper for contextmenu handling that calls both internal and external handlers
  const handleContextMenuWrapper = React.useCallback(
    (item, moreProps, e) => {
      // First call the internal handler (for selection, etc.)
      handleDrawingObjectContextMenu(item, moreProps, e);

      // Then call the external handler if provided
      if (typeof onInteractiveContextMenu === "function") {
        onInteractiveContextMenu(item, moreProps, e);
      }
    },
    [handleDrawingObjectContextMenu, onInteractiveContextMenu],
  );

  const selectedInteractiveInfo = React.useMemo(() => {
    if (!objectsMap) return null;

    for (const [type, items] of Object.entries(objectsMap)) {
      if (!Array.isArray(items)) continue;
      const index = items.findIndex((item) => item && item.selected);
      if (index >= 0) {
        return { type, index, object: items[index] };
      }
    }
    return null;
  }, [objectsMap]);

  const hoveredInteractiveInfo = React.useMemo(() => {
    if (!objectsMap) return null;
    if (typeof getInteractiveNodes !== "function") return null;

    const nodes = getInteractiveNodes();
    if (!Array.isArray(nodes) || nodes.length === 0) return null;

    for (const nodeRef of nodes) {
      const node = nodeRef.node;
      if (!node || typeof node.isHover !== "function") continue;

      let moreProps;
      try {
        if (typeof node.getMoreProps === "function") {
          moreProps = node.getMoreProps();
        } else if (node.moreProps) {
          moreProps = node.moreProps;
        }
      } catch (error) {
        moreProps = undefined;
      }

      let isHovering = false;
      try {
        isHovering = moreProps ? !!node.isHover(moreProps) : !!node.isHover();
      } catch (error) {
        isHovering = false;
      }

      if (!isHovering) continue;

      const itemsForType = objectsMap[nodeRef.type];
      if (!Array.isArray(itemsForType) || itemsForType.length === 0) {
        continue;
      }

      let candidateIndex =
        typeof node.props?.index === "number" ? node.props.index : -1;
      if (candidateIndex < 0) {
        candidateIndex = itemsForType.findIndex(
          (item) => item && item.selected,
        );
      }
      if (candidateIndex < 0) {
        candidateIndex = itemsForType.findIndex(Boolean);
      }

      const hoveredItem =
        candidateIndex >= 0 ? itemsForType[candidateIndex] : null;

      if (hoveredItem) {
        return {
          type: nodeRef.type,
          index: candidateIndex,
          object: hoveredItem,
        };
      }
    }

    return null;
  }, [objectsMap, getInteractiveNodes]);

  React.useEffect(() => {
    if (typeof onDeleteInteractive !== "function") return;
    if (!selectorEnabled) return;
    const activeInteractiveInfo =
      selectedInteractiveInfo || hoveredInteractiveInfo;
    if (!activeInteractiveInfo || !activeInteractiveInfo.object) return;

    const chartIdValue = chartState?.chartId ?? chartState?.id ?? null;
    const currentInfo = activeInteractiveInfo;

    const handleKeyDown = (event) => {
      if (!currentInfo || !currentInfo.object) return;
      // Ignore when typing inside inputs or editable elements
      const target = event.target;
      if (
        target &&
        (target.isContentEditable ||
          (target.tagName &&
            ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) ||
          (typeof target.getAttribute === "function" &&
            target.getAttribute("data-allow-delete") === "false"))
      ) {
        return;
      }

      const key = event.key;
      if (key !== "Delete" && key !== "Backspace") return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (!chartIdValue) return;

      event.preventDefault();

      const payload = {
        type: currentInfo.type,
        chartId: chartSectionId,
        object: currentInfo.object,
        index: currentInfo.index,
        moreProps: {
          chartConfig: { id: chartSectionId },
        },
      };

      onDeleteInteractive(chartIdValue, payload);
    };

    // PoC #popout: attach in every window — keys land on the focused window.
    const detach = addEventListenerAllWindows("keydown", handleKeyDown);
    return detach;
  }, [
    onDeleteInteractive,
    selectedInteractiveInfo,
    hoveredInteractiveInfo,
    selectorEnabled,
    chartSectionId,
    chartState?.chartId,
    chartState?.id,
  ]);

  return (
    <>
      {Object.entries(comps).map(([type, Comp]) => {
        const ref = refsMap[type];
        const items = objectsMap[type] ?? [];
        const propName = drawingObjectMap[type];
        // Normalize appearance: merge registry appearance with per-component defaults
        // so nested fields (stroke, fill, font) always exist and PropTypes validations
        // won't complain about undefined nested properties.
        // Issue #339: default salvo por tipo vence o swatch global; sem
        // default salvo, comportamento original (swatch pinta o objeto novo).
        const savedAppearance = customAppearanceByType[type];
        const baseAppearance = mergeCustomAppearance(
          appearances[type] || {},
          savedAppearance,
        );
        const drawColor = resolveDrawingColor(
          savedAppearance,
          currentDrawingColor,
        );

        // Generate theme-aware fill color based on the drawing color
        const strokeColor = drawColor || baseAppearance.stroke?.color || "#000";
        const themeAwareFillColor = drawColor
          ? getThemeAwareFillColor(drawColor, theme)
          : baseAppearance.fill?.color ||
            DEFAULT_APPEARANCE_COLORS.defaultFillColor;
        const themeAwareFillOpacity = getThemeAwareFillOpacity(
          theme,
          baseAppearance.fill?.opacity ??
            DEFAULT_APPEARANCE_COLORS.defaultFillOpacity,
        );

        const appearance = {
          // Preserva campos de aparência PRÓPRIOS do registry que não fazem
          // parte do template fixo abaixo (ex.: `levels` dos Fibonacci e do
          // Cálculo R/G, `labelUsaCorDaLinha`). Sem isto, esses campos eram
          // descartados e a customização não persistia (caía sempre no fallback
          // de defaults do wrapper). Os campos explícitos abaixo ainda vencem.
          ...baseAppearance,
          stroke: {
            ...(baseAppearance.stroke || {}),
            color: strokeColor,
            dash: baseAppearance.stroke?.dash || "Solid",
            width: baseAppearance.stroke?.width || 1,
            opacity:
              baseAppearance.stroke?.opacity ??
              DEFAULT_APPEARANCE_COLORS.defaultStrokeOpacity,
          },
          fill: {
            ...(baseAppearance.fill || {}),
            color: themeAwareFillColor,
            opacity: themeAwareFillOpacity,
          },
          font: {
            ...(baseAppearance.font || {}),
            fontFamily:
              baseAppearance.font?.fontFamily ||
              "Helvetica Neue, Helvetica, Arial, sans-serif",
            color: drawColor || baseAppearance.font?.color || "#000",
            fontSize: baseAppearance.font?.fontSize || 12,
          },
          bgFill: {
            color: baseAppearance.bgFill?.color || "#fff",
            opacity: baseAppearance.bgFill?.opacity ?? 1,
            ...(baseAppearance.bgFill || {}),
          },
          text: baseAppearance.text ?? (baseAppearance.text || ""),
          // Provide top-level convenience props expected by legacy interactive components
          fillOpacity: themeAwareFillOpacity,
          strokeOpacity:
            baseAppearance.stroke?.opacity ??
            baseAppearance.strokeOpacity ??
            DEFAULT_APPEARANCE_COLORS.defaultStrokeOpacity,
          strokeWidth:
            baseAppearance.stroke?.width ??
            baseAppearance.strokeWidth ??
            DEFAULT_APPEARANCE_COLORS.defaultStrokeWidth,
          // Background opacity and text fill used by InteractiveText defaultText
          bgOpacity:
            baseAppearance.bgFill?.opacity ?? baseAppearance.bgOpacity ?? 1,
          textFill:
            drawColor ||
            (baseAppearance.font?.color ?? baseAppearance.textFill ?? "#000"),
          // Provide top-level font convenience fields for legacy components like InteractiveText
          fontFamily:
            baseAppearance.font?.fontFamily ??
            baseAppearance.fontFamily ??
            "Helvetica Neue, Helvetica, Arial, sans-serif",
          fontSize:
            baseAppearance.font?.fontSize ?? baseAppearance.fontSize ?? 12,
          fontWeight:
            baseAppearance.font?.fontWeight ??
            baseAppearance.fontWeight ??
            "normal",
          fontStyle:
            baseAppearance.font?.fontStyle ??
            baseAppearance.fontStyle ??
            "normal",
        };

        // Basic props for all components
        const compProps = {
          ref,
          enabled: activeInteractive === type,
          onStart: () => {},
          onComplete: onCompleteMap[type],
          appearance,
          [propName]: items,
          chartSectionId,
        };

        if (type === "verticalLine") {
          const coordinateDefaults = {
            fill: themeColors.background,
            opacity: 1,
            textFill: themeColors.text,
            fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
            fontSize: 12,
            fontWeight: "normal",
            stroke: strokeColor,
            strokeOpacity:
              appearance.stroke?.opacity ??
              DEFAULT_APPEARANCE_COLORS.defaultStrokeOpacity,
            strokeWidth:
              appearance.stroke?.width ??
              appearance.strokeWidth ??
              DEFAULT_APPEARANCE_COLORS.defaultStrokeWidth,
            lineOpacity: 0,
            lineStroke: themeColors.border,
            rectWidth: 80,
            rectHeight: 20,
            arrowWidth: 0,
            dy: 0,
            orient: "bottom",
            at: "bottom",
          };

          compProps.appearance = {
            ...appearance,
            coordinate: {
              ...coordinateDefaults,
              ...(appearance.coordinate || {}),
            },
          };

          compProps.isLastSection = isLastSection;

          compProps.displayFormat = (value, moreProps = {}) => {
            const { fullData, xAccessor, displayXAccessor } = moreProps;

            let resolvedValue = value;
            let matchedDatum = null;

            if (Array.isArray(fullData) && typeof xAccessor === "function") {
              const directMatch = fullData.find(
                (datum) => xAccessor(datum) === value,
              );
              if (directMatch) {
                matchedDatum = directMatch;
              } else if (
                typeof value === "number" &&
                value >= 0 &&
                value < fullData.length
              ) {
                matchedDatum = fullData[value];
              }

              if (matchedDatum) {
                resolvedValue = xAccessor(matchedDatum);
                if (displayXAccessor) {
                  resolvedValue = displayXAccessor(matchedDatum);
                }
              }
            }

            if (timeDisplayFormat) {
              const formatted = timeDisplayFormat(resolvedValue, moreProps);
              if (formatted !== undefined && formatted !== null)
                return formatted;
            }

            const date =
              resolvedValue instanceof Date
                ? resolvedValue
                : new Date(resolvedValue);
            if (!Number.isNaN(date.getTime())) {
              return date.toLocaleString("pt-BR", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              });
            }

            return String(resolvedValue);
          };
        }

        if (type === "horizontalLine") {
          const coordinateDefaults = {
            fill: themeColors.background,
            opacity: 1,
            textFill: themeColors.text,
            fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
            fontSize: 12,
            fontWeight: "normal",
            stroke: strokeColor,
            strokeOpacity:
              appearance.stroke?.opacity ??
              DEFAULT_APPEARANCE_COLORS.defaultStrokeOpacity,
            strokeWidth:
              appearance.stroke?.width ??
              appearance.strokeWidth ??
              DEFAULT_APPEARANCE_COLORS.defaultStrokeWidth,
            lineOpacity: 0,
            lineStroke: themeColors.border,
            rectWidth: 60,
            rectHeight: 20,
            arrowWidth: 0,
            dx: 0,
            orient: "right",
            at: "right",
          };

          compProps.appearance = {
            ...appearance,
            coordinate: {
              ...coordinateDefaults,
              ...(appearance.coordinate || {}),
            },
          };

          compProps.isLastSection = isLastSection;

          compProps.displayFormat = (value) => {
            if (priceDisplayFormat) {
              const formatted = priceDisplayFormat(value);
              if (formatted !== undefined && formatted !== null)
                return formatted;
            }
            const num = Number(value);
            return Number.isFinite(num) ? num.toFixed(2) : String(value);
          };
        }

        // Add special props for InteractiveText
        if (type === "interactiveText") {
          compProps.defaultText = {
            ...appearance,
            text: "Lorem ipsum...",
          };
          compProps.onChoosePosition = onCompleteMap[type];
          compProps.onDragComplete = onCompleteMap[type];
          compProps.onSelect = handleDrawingObjectSelect;
          // Pass the main chart ID for proper ID generation
          compProps.mainChartId = chartState?.chartId || "default";
          // Add function to open edit modal directly
          compProps.onOpenEditModal = (interactiveId) => {
            if (onOpenEditModal) {
              onOpenEditModal(interactiveId);
            }
          };
        }

        // Add special props for InteractiveYCoordinate
        if (type === "interactiveYCoordinate") {
          compProps.defaultPriceCoordinate = {
            ...appearance,
            text: "0.00",
          };
          compProps.onChoosePosition = onCompleteMap[type];
          compProps.onDragComplete = onCompleteMap[type];

          // Ensure strokeDasharray exists (InteractiveYCoordinate expects this)
          compProps.defaultPriceCoordinate.strokeDasharray =
            compProps.defaultPriceCoordinate.strokeDasharray ??
            compProps.defaultPriceCoordinate.stroke?.dash ??
            "ShortDash2";

          // Ensure textBox exists (required by InteractiveYCoordinate)
          compProps.defaultPriceCoordinate.textBox = compProps
            .defaultPriceCoordinate.textBox ?? {
            height: 24,
            left: 20,
            padding: { left: 10, right: 5 },
            closeIcon: {
              padding: { left: 5, right: 8 },
              width: 8,
            },
          };

          // Ensure edge object exists with required shape
          compProps.defaultPriceCoordinate.edge =
            compProps.defaultPriceCoordinate.edge ?? {};

          // Helper: extract color string from either a string or registry-style object
          const extractColorString = (val, fallback) => {
            if (typeof val === "string") return val;
            if (val && typeof val === "object") {
              if (val.color) return val.color;
              // Some registry shapes may put color as `value` or nested; try common keys
              if (val.value && typeof val.value === "string") return val.value;
            }
            return fallback;
          };

          const edgeStroke = extractColorString(
            compProps.defaultPriceCoordinate.stroke ??
              compProps.defaultPriceCoordinate.edge.stroke,
            "#6574CD",
          );
          const edgeFill = extractColorString(
            compProps.defaultPriceCoordinate.bgFill ??
              compProps.defaultPriceCoordinate.edge.fill,
            "#FFFFFF",
          );

          compProps.defaultPriceCoordinate.edge = {
            stroke: edgeStroke,
            strokeOpacity:
              compProps.defaultPriceCoordinate.strokeOpacity ??
              compProps.defaultPriceCoordinate.edge.strokeOpacity ??
              1,
            strokeWidth:
              compProps.defaultPriceCoordinate.strokeWidth ??
              compProps.defaultPriceCoordinate.edge.strokeWidth ??
              1,
            fill: edgeFill,
            fillOpacity:
              compProps.defaultPriceCoordinate.bgOpacity ??
              compProps.defaultPriceCoordinate.edge.fillOpacity ??
              1,
            orient: "right",
            at: "right",
            arrowWidth: 10,
            dx: 0,
            rectWidth: 50,
            rectHeight: 20,
          };
        }

        // Per-component normalization for legacy expectations
        if (type === "gannFan") {
          // Ensure top-level strokeWidth and font properties exist
          compProps.appearance = compProps.appearance || {};
          compProps.appearance.strokeWidth =
            compProps.appearance.strokeWidth ??
            compProps.appearance.stroke?.width ??
            DEFAULT_APPEARANCE_COLORS.defaultStrokeWidth;
          compProps.appearance.fontFamily =
            compProps.appearance.fontFamily ??
            compProps.appearance.font?.fontFamily ??
            "Helvetica Neue, Helvetica, Arial, sans-serif";
          compProps.appearance.fontSize =
            compProps.appearance.fontSize ??
            compProps.appearance.font?.fontSize ??
            12;
          compProps.appearance.fontFill =
            compProps.appearance.fontFill ??
            compProps.appearance.font?.color ??
            "#000";

          // Normalize fill to an array (GannFan expects an array of colors)
          const baseFill = baseAppearance.fill;
          if (Array.isArray(baseFill)) {
            compProps.appearance.fill = baseFill;
          } else if (baseFill && typeof baseFill === "object") {
            compProps.appearance.fill = [baseFill.color ?? baseFill];
          } else if (typeof baseFill === "string") {
            compProps.appearance.fill = [baseFill];
          } else if (
            !compProps.appearance.fill ||
            compProps.appearance.fill.length === 0
          ) {
            compProps.appearance.fill = [
              DEFAULT_APPEARANCE_COLORS.defaultFillColor,
            ];
          }
        }

        // Continuous tool needs onFinish callback to exit drawing mode on double-click
        if (type === "continuous") {
          compProps.onFinish = onFinishDrawing;
        }

        // StopLine - price alert line (1/3 width, right-aligned)
        if (type === "stopLine") {
          const coordinateDefaults = {
            fill: appearance.stroke?.color || strokeColor || "#E91E63",
            opacity: 1,
            textFill: "#FFFFFF",
            fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
            fontSize: 12,
            fontWeight: "normal",
            stroke: appearance.stroke?.color || strokeColor || "#E91E63",
            strokeOpacity:
              appearance.stroke?.opacity ??
              DEFAULT_APPEARANCE_COLORS.defaultStrokeOpacity,
            strokeWidth:
              appearance.stroke?.width ??
              appearance.strokeWidth ??
              DEFAULT_APPEARANCE_COLORS.defaultStrokeWidth,
            lineOpacity: 0,
            lineStroke: themeColors.border,
            rectWidth: 60,
            rectHeight: 20,
            arrowWidth: 0,
            dx: 0,
            orient: "right",
            at: "right",
          };

          compProps.appearance = {
            ...appearance,
            coordinate: {
              ...coordinateDefaults,
              ...(appearance.coordinate || {}),
            },
          };

          // Use chartSymbol prop for price formatting
          const symbol = chartSymbol || "";

          compProps.displayFormat = (value) => {
            // Use global formatPrice that respects symbol's tick size
            if (symbol) {
              return formatPriceGlobal(symbol, value);
            }
            if (priceDisplayFormat) {
              const formatted = priceDisplayFormat(value);
              if (formatted !== undefined && formatted !== null)
                return formatted;
            }
            const num = Number(value);
            return Number.isFinite(num) ? num.toFixed(2) : String(value);
          };

          // Get current price from chartState.data for crossing detection
          const chartData = chartState?.data;
          const lastItem =
            Array.isArray(chartData) && chartData.length > 0
              ? chartData[chartData.length - 1]
              : null;
          const currentPrice = lastItem?.close ?? lastItem?.Close ?? null;
          compProps.currentPrice = currentPrice;
          compProps.symbol = symbol;

          // Price crossing callback - will trigger alerts
          compProps.onPriceCrossing = (crossingInfo) => {
            // Dispatch custom event for the alert service to handle
            window.dispatchEvent(
              new CustomEvent("stopLinePriceCrossing", {
                detail: {
                  ...crossingInfo,
                  chartId: chartState?.chartId || "default",
                },
              }),
            );
          };
        }

        return <Comp key={type} {...compProps} />;
      })}

      <DrawingObjectSelector
        getInteractiveNodes={getInteractiveNodes}
        drawingObjectMap={drawingObjectMap}
        onSelect={handleDrawingObjectSelect}
        onDoubleClick={handleDrawingObjectDoubleClick}
        onContextMenu={handleContextMenuWrapper}
        enabled={selectorEnabled}
      />
    </>
  );
}

// Optimization: Skip re-render when only chartState.data changes
// Interactive tools don't need to update on every quote
function arePropsEqual(prevProps, nextProps) {
  // Always re-render if these change
  if (prevProps.selectorEnabled !== nextProps.selectorEnabled) return false;
  if (prevProps.chartSymbol !== nextProps.chartSymbol) return false;
  if (prevProps.chartSectionId !== nextProps.chartSectionId) return false;
  if (prevProps.isLastSection !== nextProps.isLastSection) return false;
  if (prevProps.onInteractiveApiReady !== nextProps.onInteractiveApiReady)
    return false;
  if (prevProps.onInteractiveContextMenu !== nextProps.onInteractiveContextMenu)
    return false;
  if (prevProps.onOpenEditModal !== nextProps.onOpenEditModal) return false;
  if (prevProps.onDeleteInteractive !== nextProps.onDeleteInteractive)
    return false;

  // For chartState, only care about chartId changes, not data changes
  const prevChartId = prevProps.chartState?.chartId || prevProps.chartState?.id;
  const nextChartId = nextProps.chartState?.chartId || nextProps.chartState?.id;
  if (prevChartId !== nextChartId) return false;

  // Skip re-render - props are equal enough for interactive layer
  return true;
}

const MemoizedInteractiveLayer = React.memo(InteractiveLayer, arePropsEqual);

MemoizedInteractiveLayer.propTypes = {
  components: PropTypes.object,
  appearanceMap: PropTypes.object,
  selectorEnabled: PropTypes.bool,
  chartSectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  chartState: PropTypes.object.isRequired,
  chartSymbol: PropTypes.string,
  interactiveApi: PropTypes.object,
  onInteractiveApiReady: PropTypes.func,
  onInteractiveContextMenu: PropTypes.func,
  onOpenEditModal: PropTypes.func,
  onDeleteInteractive: PropTypes.func,
  timeDisplayFormat: PropTypes.func,
  priceDisplayFormat: PropTypes.func,
  isLastSection: PropTypes.bool,
};

export default MemoizedInteractiveLayer;
